import { useEffect, useRef } from 'react';
import { BroadcastChannel } from 'broadcast-channel';

import type { TBankAuthConfig } from 'src/components/AuthNew/utils/socialNetworkUtils';
import { createTBankAuthUrl } from '../utils/createTbankAuthUrl';

const isWebView = window.is_webview || false;

type TBankAuthFailPayload = {
  errorId?: string;
  errorCode?: string;
  errorMessage?: string;
  errorDetails?: string;
};

type TBankAuthSuccessPayload = {
  state?: string;
  code?: string;
  session_state?: string;
  redirect_url?: string;
};

export type TBankAuthPayload = TBankAuthFailPayload & TBankAuthSuccessPayload;

type TBankAuthResult = {
  success: boolean;
  payload: TBankAuthPayload;
};

type TBankAuthOnSuccess = (result: TBankAuthResult) => void;
type TBankAuthOnError = () => void;

/**
 * @desc процесс авторизации следующий:
 * 1. в новом окне открывается ссылка для авторизации на стороне TBank
 * 2. создается Broadcast канал для обмена сообщениями между окном инициатором авторизации
 * и окном, где происходит запрос кода TBank
 * 3. после подтверждения доступа, TBank перенаправляет на redirect_url из ссылки и добавляет code или ошибки в параметры
 * 4. на странице шаблона (app/views/social_login/tbank.haml) скрипт считывает code или ошибки и отправляет их по Broadcast каналу
 * 5. хук передает полученные данные вызываемому методу.
 * */
export const useTBankAuth = () => {
  /** ссылка для авторизации в tbank */
  /** полученный от TBank код авторизации */
  const authCodeRef = useRef<TBankAuthResult | undefined>();
  const authChannel = useRef<BroadcastChannel>();

  const resetAuthCodeRef = (): void => {
    authCodeRef.current = undefined;
  };

  /** открыть окно для авторизации через TBank */
  const openAuthPopup = (url: string): Window | null =>
    window.open(url, '_blank', 'popup');

  /** прослушивание закрытия окна авторизации TBank */
  const createCloseAuthPopupListener =
    (onError: TBankAuthOnError) =>
    (instance: Window | null): void => {
      if (!instance) {
        return;
      }
      const intID = setInterval(() => {
        if (instance?.closed) {
          clearInterval(intID);
          if (!authCodeRef.current || !authCodeRef.current?.success) {
            closeAuthChannel();
            onError();
          }
        }
      }, 500);
    };

  /** обработка ответа из результирующего окна авторизации через TBank */
  const createAuthChannelResultHandler =
    (onSuccess: TBankAuthOnSuccess, onError: TBankAuthOnError) =>
    (result: TBankAuthResult) => {
      if (!result?.success) {
        // eslint-disable-next-line no-console
        console.error('TBank AUTH NOT SUCCESS: ', result);
        onError();
        return;
      }
      authCodeRef.current = result;
      onSuccess(result);
      closeAuthChannel();
    };

  /**
   * Открыть канал для общения между окном инициатором авторизации
   * и окном в котором происходит получение кода от TBank
   * */
  const openAuthChannel = (handler) => {
    const channel = new BroadcastChannel('auth-tbank-code');
    authChannel.current = channel;
    channel.addEventListener('message', handler);
  };

  const closeAuthChannel = () => {
    authChannel.current?.close();
  };

  const handleTBankLogin = (
    config: TBankAuthConfig,
    onSuccess: TBankAuthOnSuccess,
    onError: TBankAuthOnError,
  ): void => {
    try {
      const authURL = createTBankAuthUrl(config, isWebView);

      if (isWebView) {
        // редирект в текущей вкладке
        window.location.href = authURL;
      } else {
        closeAuthChannel();
        resetAuthCodeRef();

        const popup = openAuthPopup(authURL);

        const listenCloseAuthPopup = createCloseAuthPopupListener(onError);
        listenCloseAuthPopup(popup);
        openAuthChannel(createAuthChannelResultHandler(onSuccess, onError));
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  /** закрытие канала обмена сообщениями */
  useEffect(() => closeAuthChannel, []);

  return {
    handleTBankLogin,
  };
};
